import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { del, errorMessage, get, put } from "../../api";
import { SignaturePad } from "../../components/signature-pad";
import { Alert, Button, Loading, PageHeader, Panel, PanelHeader } from "../../components/ui";

/** Trims the empty space around a drawn or uploaded signature, so it sits right on the line. */
function trimmed(src: HTMLCanvasElement): string {
  const ctx = src.getContext("2d")!;
  const { width: w, height: h } = src;
  const data = ctx.getImageData(0, 0, w, h).data;
  let [x0, y0, x1, y1] = [w, h, -1, -1];
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++)
      if (data[(y * w + x) * 4 + 3]! > 16) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
  if (x1 < 0) return "";
  const pad = 6;
  const out = document.createElement("canvas");
  out.width = Math.min(w, x1 - x0 + pad * 2);
  out.height = Math.min(h, y1 - y0 + pad * 2);
  out.getContext("2d")!.drawImage(src, Math.max(0, x0 - pad), Math.max(0, y0 - pad), out.width, out.height, 0, 0, out.width, out.height);
  return out.toDataURL("image/png");
}

/** An uploaded image as a PNG with a see-through background (white or near-white paper drops out). */
async function fromUpload(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 1200 / bitmap.width);
  const c = document.createElement("canvas");
  c.width = Math.round(bitmap.width * scale);
  c.height = Math.round(bitmap.height * scale);
  const ctx = c.getContext("2d")!;
  ctx.drawImage(bitmap, 0, 0, c.width, c.height);
  const img = ctx.getImageData(0, 0, c.width, c.height);
  for (let i = 0; i < img.data.length; i += 4) {
    const light = (img.data[i]! + img.data[i + 1]! + img.data[i + 2]!) / 3;
    if (light > 200) img.data[i + 3] = 0;
  }
  ctx.putImageData(img, 0, 0);
  return trimmed(c);
}

/** My profile → Signature: drawn or uploaded, used where you sign for NMT. */
/** `embedded`: a section of My profile, under its own small heading. */
export function MySignature({ embedded = false }: { embedded?: boolean } = {}) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["my-signature"], queryFn: () => get<{ url: string | null }>("/api/me/signature") });
  const pad = useRef<HTMLCanvasElement>(null);
  const [drawn, setDrawn] = useState(false);
  const [mode, setMode] = useState<"view" | "draw">("view");
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const done = (d: { url: string | null }) => (qc.setQueryData(["my-signature"], d), setMode("view"), setDrawn(false));
  const save = useMutation({ mutationFn: (image: string) => put<{ url: string | null }>("/api/me/signature", { image }), onSuccess: done });
  const remove = useMutation({ mutationFn: () => del<{ url: string | null }>("/api/me/signature"), onSuccess: done });
  if (q.isLoading) return <Loading />;
  const url = q.data?.url ?? null;
  return (
    <div className="flex flex-col gap-5">
      {embedded ? (
        <div>
          <h2 className="text-lg font-semibold">Signature</h2>
          <p className="max-w-prose text-sm text-muted">Used on documents you sign for NMT: proposals (when you're the one who signs them, set in Settings → Company) and completion certificates you issue.</p>
        </div>
      ) : (
        <PageHeader title="My signature" description="Used on documents you sign for NMT: proposals (when you're the one who signs them, set in Settings → Company) and completion certificates you issue." />
      )}
      <Panel>
        <PanelHeader title={url ? "Your signature" : "No signature yet"} description={url ? undefined : "Draw it, or upload a picture of it (a photo of your signature on white paper works)."} />
        <div className="flex flex-col gap-4 px-5 py-5">
          {(err || save.error || remove.error) && <Alert>{err ?? errorMessage(save.error ?? remove.error)}</Alert>}
          {url && mode === "view" && (
            <div className="rounded-[var(--radius-control)] border border-line bg-white p-4">
              <img src={url} alt="Your signature" className="max-h-24 max-w-full" />
            </div>
          )}
          {mode === "draw" && <SignaturePad canvas={pad} onDrawn={setDrawn} label="Draw your signature" />}
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (!f) return;
              setErr(null);
              try {
                const png = await fromUpload(f);
                if (!png) return setErr("Couldn't find a signature in that picture. Try a darker pen on white paper.");
                save.mutate(png);
              } catch {
                setErr("That picture couldn't be read. Try a PNG or JPG.");
              }
            }}
          />
          <div className="flex flex-wrap gap-2">
            {mode === "draw" ? (
              <>
                <Button variant="primary" disabled={!drawn} busy={save.isPending} onClick={() => pad.current && save.mutate(trimmed(pad.current))}>
                  Save signature
                </Button>
                <Button variant="quiet" onClick={() => setMode("view")}>
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button variant={url ? "secondary" : "primary"} onClick={() => setMode("draw")}>
                  Draw {url ? "a new one" : "it"}
                </Button>
                <Button busy={save.isPending} onClick={() => fileRef.current?.click()}>
                  Upload a picture
                </Button>
                {url && (
                  <Button variant="quiet" busy={remove.isPending} onClick={() => window.confirm("Remove your signature? Documents will show your name instead.") && remove.mutate()}>
                    Remove
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </Panel>
    </div>
  );
}
